// ============================================
// TABLEAU DE BORD CANDIDAT
// ============================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Box,
    Typography,
    Grid,
    Paper,
    Card,
    CardContent,
    Button,
    CircularProgress,
    Alert
} from '@mui/material';
import {
    Description,
    CheckCircle,
    HourglassEmpty,
    Cancel,
    Notifications
} from '@mui/icons-material';
import candidateService from '../services/candidateService';
import { toast } from 'react-toastify';

const CandidateDashboard = () => {
    const navigate = useNavigate();
    const [dashboard, setDashboard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        loadDashboard();
    }, []);

    const loadDashboard = async () => {
        try {
            const response = await candidateService.getDashboard();
            setDashboard(response.data);
        } catch (err) {
            setError(err.message || 'Erreur lors du chargement du tableau de bord');
            toast.error('Erreur de chargement');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Container>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container>
                <Box sx={{ mt: 4 }}>
                    <Alert severity="error">{error}</Alert>
                </Box>
            </Container>
        );
    }

    // Si pas de profil candidat
    if (!dashboard?.hasProfile) {
        return (
            <Container>
                <Box sx={{ mt: 4 }}>
                    <Alert severity="info" sx={{ mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Bienvenue ! Complétez votre profil pour continuer
                        </Typography>
                        <Typography variant="body2" paragraph>
                            Pour soumettre une candidature, vous devez d'abord compléter votre profil candidat.
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={() => navigate('/profile')}
                            sx={{ backgroundColor: '#003366' }}
                        >
                            Compléter mon profil
                        </Button>
                    </Alert>
                </Box>
            </Container>
        );
    }

    const stats = dashboard.stats || {};

    return (
        <Container maxWidth="lg">
            <Box sx={{ mt: 4, mb: 6 }}>
                <Typography variant="h4" gutterBottom sx={{ color: '#003366', fontWeight: 'bold' }}>
                    Mon Tableau de Bord
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                    Vue d'ensemble de vos candidatures et activités
                </Typography>

                {/* Statistiques */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card elevation={2}>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <Description sx={{ fontSize: 40, color: '#003366', mb: 1 }} />
                                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#003366' }}>
                                    {stats.total || 0}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Total candidatures
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card elevation={2}>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <HourglassEmpty sx={{ fontSize: 40, color: '#FFA500', mb: 1 }} />
                                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#FFA500' }}>
                                    {stats.submitted || 0}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    En attente
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card elevation={2}>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <CheckCircle sx={{ fontSize: 40, color: '#4CAF50', mb: 1 }} />
                                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#4CAF50' }}>
                                    {stats.approved || 0}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Approuvées
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card elevation={2}>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <Cancel sx={{ fontSize: 40, color: '#F44336', mb: 1 }} />
                                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#F44336' }}>
                                    {stats.rejected || 0}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Rejetées
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Actions rapides */}
                <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                        Actions Rapides
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={() => navigate('/profile')}
                                sx={{ backgroundColor: '#003366' }}
                            >
                                Mon Profil
                            </Button>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={() => navigate('/applications')}
                                sx={{ backgroundColor: '#003366' }}
                            >
                                Mes Candidatures
                            </Button>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={() => navigate('/announcements')}
                                sx={{ borderColor: '#003366', color: '#003366' }}
                            >
                                Voir Annonces
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Candidatures récentes */}
                <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                        Candidatures Récentes
                    </Typography>
                    {dashboard.recentApplications && dashboard.recentApplications.length > 0 ? (
                        dashboard.recentApplications.map((app) => (
                            <Box
                                key={app.application_id}
                                sx={{
                                    p: 2,
                                    mb: 2,
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 1,
                                    '&:hover': { backgroundColor: '#f5f5f5' }
                                }}
                            >
                                <Grid container alignItems="center">
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                            {app.competition_title}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            N° {app.application_number}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: app.status === 'approved' ? '#4CAF50' : '#FFA500',
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            {app.status === 'draft' && 'Brouillon'}
                                            {app.status === 'submitted' && 'Soumise'}
                                            {app.status === 'under_review' && 'En révision'}
                                            {app.status === 'approved' && 'Approuvée'}
                                            {app.status === 'rejected' && 'Rejetée'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={3} sx={{ textAlign: 'right' }}>
                                        <Button
                                            size="small"
                                            onClick={() => navigate(`/applications/${app.application_id}`)}
                                        >
                                            Voir détails
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Box>
                        ))
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            Aucune candidature pour le moment
                        </Typography>
                    )}
                </Paper>

                {/* Notifications */}
                <Paper elevation={2} sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                        <Notifications sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Notifications Récentes
                    </Typography>
                    {dashboard.notifications && dashboard.notifications.length > 0 ? (
                        dashboard.notifications.slice(0, 5).map((notif) => (
                            <Box
                                key={notif.notification_id}
                                sx={{
                                    p: 2,
                                    mb: 1,
                                    backgroundColor: notif.is_read ? '#f5f5f5' : '#e3f2fd',
                                    borderRadius: 1
                                }}
                            >
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                    {notif.title}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {notif.message}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {new Date(notif.created_at).toLocaleDateString('fr-FR')}
                                </Typography>
                            </Box>
                        ))
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            Aucune notification
                        </Typography>
                    )}
                </Paper>
            </Box>
        </Container>
    );
};

export default CandidateDashboard;

